<template>
  <div v-loading="$wait.waiting(_uid)" class="max-w-screen-xl m-auto">
    <div v-if="isMounted">
      <div v-if="!shouldPreventTraining">
        <div v-if="user.has_valid_subscription">

          <div v-if="reminderList.length" class="card-wrapper mb-5">
            <div v-for="(reminder, index) in reminderList" :key="index">
              <Reminder :type="reminder.id" :item="reminder" />
            </div>
          </div>

          <div class="flex flex-wrap flex-col xl:flex-row">
            <Regular v-if="lesson && !lesson.is_suspendable" class="flex-1" :prop-lesson="lesson"
              @launch-training="launch" />

            <Suspendable v-if="lesson && lesson.is_suspendable" class="flex-1" :prop-lesson="lesson"
              @launch-training="launch" @suspend-training="suspend" />

            <InternationalProcedure v-if="internationalProcedureLesson" class="flex-1 xl:ml-10 mt-10 xl:mt-0"
              :prop-lesson="internationalProcedureLesson" @launch-training="launch" />
          </div>

          <div v-if="showNextLesson" class="alert alert-info">
            <p>
              Periodically, our system adjusts your schedule to ensure smooth delivery.
            </p>
            <p>
              Based on the completion of your last lesson, your next lesson will be available at midnight UTC on <b>{{
                formatDate(nextLesson.start_date) }}</b>.
            </p>
          </div>

          <div v-if="showNoTraining" class="card-wrapper">
            <Alert :type="'info'" :icon="'far fa-check-circle'">
              <b>No training available.</b>
            </Alert>
          </div>

          <div v-if="showNoAditionalTraining" class="card-wrapper">
            <Alert :type="'info'" :icon="'far fa-check-circle'">
              <b>No aditional training available.</b>
            </Alert>
          </div>
        </div>

        <div v-else>
          <Alert :type="'danger'">
            <p class="text-3xl">
              <b>
                <p>
                  To continue training, please renew your subscription. <br>
                  Contact
                  <a href="mailto:support@trainingport.net">support</a>
                  if you need assistance or require further information.
                </p>
              </b>
            </p>
          </Alert>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { getStorageData } from "@/utils/storage";
import MyService from "@app/services/my";
import RegistrationService from "@app/services/registration";
import InternationalProcedure from "@components/Dashboard/Views/Pages/Training/Components/Lessons/InternationalProcedure.vue";
import Regular from "@components/Dashboard/Views/Pages/Training/Components/Lessons/Regular.vue";
import Suspendable from "@components/Dashboard/Views/Pages/Training/Components/Lessons/Suspendable.vue";
import Reminder from "@components/Dashboard/Views/Pages/Training/Components/Reminder.vue";
import Alert from "@components/UIComponents/Alert";
import HelperDate from "@helpers/dates";
import { mapActions, mapGetters } from "vuex";

export default {
  components: { Alert, Reminder, Regular, Suspendable, InternationalProcedure },

  data() {
    const user = getStorageData('user')
    return {
      isMounted: false,
      showNextLesson: false,
      showNoTraining: false,
      showNoAditionalTraining: false,
      regularLesson: null,
      suspendLesson: null,
      internationalProcedureLesson: null,
      lesson: null,
      user: user,
      shouldPreventTraining: false,
    };
  },
  computed: {
    ...mapGetters("loadingStore", { isLoading: "loading" }),
    ...mapGetters("announcementStore", { announcementList: "list" }),
  },
  async mounted() {
    try {
      this.$wait.start(this._uid);

      this.training = await MyService.getTraining();
      await this.preventableTraining(this.announcementList);

      this.reminderList = this.training.reminderList;

      this.nextLesson = this.training.nextLesson;

      const lessonList = this.training.lessonList;

      if (!lessonList.length && !this.nextLesson) {
        this.showNoTraining = true;
      }

      if (!lessonList.length && this.nextLesson) {
        this.showNextLesson = true;
      }

      const compressedList = this.resolveCompressedLesson(lessonList);

      if (compressedList.length >= 1) {
        this.lessonList = compressedList;
        this.lesson = this.lessonList.shift();
        this.isMounted = true;
        return;
      }

      const internationalProcedureList = this.resolveInternationalProceduresLesson(lessonList);
      this.internationalProcedureLesson = internationalProcedureList.shift();

      this.lessonList = this.resolveRegularLessons(lessonList);
      this.lesson = this.lessonList.shift();

      this.isMounted = true;
    } catch (error) {
      this.addNewError(error);
    } finally {
      this.$wait.end(this._uid);
    }
  },
  methods: {
    ...mapActions("loadingStore", ["setLoading"]),
    ...mapActions("errorStore", ["addNewError"]),
    formatDate(date) {
      return HelperDate.formatDate(date);
    },
    resolveRegularLessons(lessonList) {
      // const expiredLessons = lessonList.filter(function (lesson) {
      //   return lesson.isExpired;
      // });

      // if (expiredLessons.length >= 1) {
      //   return expiredLessons;
      // }

      return lessonList.filter(function (lesson) {
        if (lesson.is_international_procedures) {
          return;
        }
        return lesson;
      });
    },
    resolveCompressedLesson(lessonList) {
      return lessonList.filter(function (lesson) {
        if (!lesson.is_compressed) {
          return;
        }
        return lesson;
      });
    },
    resolveInternationalProceduresLesson(lessonList) {
      return lessonList.filter(function (lesson) {
        if (!lesson.is_international_procedures) {
          return;
        }
        return lesson;
      });
    },
    async launch(lesson) {
      try {
        this.$wait.start(this._uid);

        const registration = lesson.registration;

        const launch = await RegistrationService.launch(registration);

        return (window.location.href = launch.launch_link);
      } catch (error) {
        this.addNewError(error);
      } finally {
        this.$wait.end(this._uid);
      }
    },
    suspend() {
      const hasAvailableLesson = this.lessonList.length >= 1;

      if (!hasAvailableLesson && !this.nextLesson) {
        return (this.showNoAditionalTraining = true);
      }

      if (!hasAvailableLesson && this.nextLesson) {
        return (this.showNextLesson = true);
      }

      return (this.lesson = this.lessonList.shift());
    },
    async preventableTraining(announcementList) {
      const training = this.training;

      if (training.preventTraining) {
        this.shouldPreventTraining = training.preventTraining;
        return;
      }

      const company = getStorageData("company");

      announcementList.forEach((announcement) => {
        const companyList = announcement.company_list;

        const found = companyList.find(({ id }) => company.id === id);

        if (!found) {
          return;
        }

        if (!announcement.prevent_training) {
          return;
        }

        return this.shouldPreventTraining = announcement.prevent_training;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass/paper/variables";
</style>
